import React, { useState } from "react";
import dayjs from "dayjs";
import {
  ScrollContainer,
  StyledTable,
  Td,
  Th,
} from "./TransactionCreateCards.styled";
import { Condition } from "../../Cards/Condition";
import { Langugage } from "../../Cards/Language";
import {
  CardToUploadByUserIdCardType,
  CardToUploadStatusCardMarketEnum,
  CardToUploadStatusCardTraderEnum,
} from "magic-shared-types";
import { useAddCommas } from "../../../utils";
import { selectGame } from "../../../store/slices/global";
import { useSelector } from "react-redux";
import { StatusEditable } from "../StatusEditable/StatusEditable";
import { useUpdateUploadStatusMutation } from "../../../services/myServer/cardToUpload";
import toast from "react-hot-toast";
import { ScrollStyles } from "../../common/common.style";

interface TransactionCreateCardsProps {
  data: CardToUploadByUserIdCardType[];
  refetch: () => void;
}

export const TransactionCreateCards: React.FC<TransactionCreateCardsProps> = ({
  data,
  refetch,
}) => {
  const [isEditStatusCm, setIsEditStatusCm] = useState(false);
  const [isEditStatusCt, setIsEditStatusCt] = useState(false);
  const [updateUploadStatus] = useUpdateUploadStatusMutation();
  const addCommas = useAddCommas();
  const game = useSelector(selectGame);
  const hasFoil = !["yugioh", "onepiece", "pokemon"].includes(
    game.card_trader_game
  );
  const hasFirstEdition = ["yugioh", "pokemon"].includes(game.card_trader_game);
  const hasReverse = ["pokemon"].includes(game.card_trader_game);

  const cardTraderStatusArray = Object.values(CardToUploadStatusCardTraderEnum);
  const cardMarketStatusArray = Object.values(CardToUploadStatusCardMarketEnum);

  const onConfirmNewStatusCt = async (
    status: CardToUploadStatusCardTraderEnum,
    id: number
  ) => {
    setIsEditStatusCt(false);
    try {
      await updateUploadStatus({ uploadId: id, status_ct: status });
      toast.success("Status changed succesfully");
      refetch();
    } catch (e) {
      toast.success("Error during change status");
    }
  };

  const onConfirmNewStatusCm = async (
    status: CardToUploadStatusCardMarketEnum,
    id: number
  ) => {
    setIsEditStatusCt(false);
    try {
      await updateUploadStatus({ uploadId: id, status_cm: status });
      toast.success("Status changed succesfully");
      refetch();
    } catch (e) {
      toast.success("Error during change status");
    }
  };

  return (
    <ScrollContainer>
      <StyledTable>
        <thead>
          <tr>
            <Th>ID</Th>
            <Th>Name</Th>
            <Th>Status ct</Th>
            <Th>Status cm</Th>
            <Th>Blueprint ID</Th>
            <Th>Card Market ID</Th>
            <Th>Language</Th>
            <Th>Quantity</Th>
            <Th>Condition</Th>
            <Th>Price</Th>
            {hasFoil && <Th>Foil</Th>}
            {hasFirstEdition && <Th>First Edition</Th>}
            {hasReverse && <Th>Reverse</Th>}
            <Th>Signed</Th>
            <Th>Altered</Th>
            <Th>Description</Th>
            <Th>Created At</Th>
            <Th>Error cm</Th>
            <Th>Error ct</Th>
            <Th>Game</Th>
          </tr>
        </thead>
        <tbody>
          {data?.map((card) => (
            <tr key={card.id}>
              <Td>{card.id}</Td>
              <Td>{card.card_name}</Td>
              <Td>
                <StatusEditable
                  statuses={cardTraderStatusArray}
                  status={card.status_ct}
                  isEditStatus={isEditStatusCt}
                  setIsEditStatus={setIsEditStatusCt}
                  onConfirmNewStatus={(status, id) =>
                    onConfirmNewStatusCt(
                      status as CardToUploadStatusCardTraderEnum,
                      id
                    )
                  }
                  id={card.id}
                />
              </Td>
              <Td>
                <StatusEditable
                  statuses={cardMarketStatusArray}
                  status={card.status_cm}
                  isEditStatus={isEditStatusCm}
                  setIsEditStatus={setIsEditStatusCm}
                  onConfirmNewStatus={(status, id) =>
                    onConfirmNewStatusCm(
                      status as CardToUploadStatusCardMarketEnum,
                      id
                    )
                  }
                  id={card.id}
                />
              </Td>
              <Td>
                <a
                  href={`https://www.cardtrader.com/cards/${card.blue_print_id}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  {card.blue_print_id}
                </a>
              </Td>
              <Td>
                {card.card_market_id ? (
                  <a
                    href={`https://www.cardmarket.com/en/${game.card_market_game}/Products?idProduct=${card.card_market_id}`}
                    target="_blank"
                    rel="noreferrer"
                  >
                    {card.card_market_id}
                  </a>
                ) : (
                  "-"
                )}
              </Td>
              <Td>
                <Langugage language={card.language} />
              </Td>
              <Td>{card.quantity}</Td>
              <Td>
                <Condition condition={card.condition} />
              </Td>
              <Td>{addCommas(card.price)} €</Td>
              {hasFoil && <Td>{card.is_foil ? "Yes" : "No"}</Td>}
              {hasFirstEdition && (
                <Td>{card.is_first_edition ? "Yes" : "No"}</Td>
              )}
              {hasReverse && <Td>{card.is_reverse ? "Yes" : "No"}</Td>}
              <Td>{card.is_signed ? "Yes" : "No"}</Td>
              <Td>{card.is_altered ? "Yes" : "No"}</Td>
              <Td css={{ width: 220 }}>{card.description || "-"}</Td>
              <Td>{dayjs(card.createdAt).format("DD MM HH:mm:ss")}</Td>
              <Td
                css={{
                  maxWidth: 200,
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  ...ScrollStyles,
                }}
              >
                <div style={{ display: "inline-block", minWidth: "100%" }}>
                  {card.error_cm ? JSON.stringify(card.error_cm, null, 2) : ""}
                </div>
              </Td>
              <Td
                css={{
                  maxWidth: 200,
                  overflowX: "auto",
                  whiteSpace: "nowrap",
                  ...ScrollStyles,
                }}
              >
                <div style={{ display: "inline-block", minWidth: "100%" }}>
                  {card.error_ct ? JSON.stringify(card.error_ct, null, 2) : ""}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </StyledTable>
    </ScrollContainer>
  );
};
